import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2, Clock, FileText, Lock, Plus, Send, AlertTriangle, History, Download, Users, X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { API, axios } from '../../utils/api';
import BottoniCarica from './BottoniCarica';
import ConfermaDocumento from './ConfermaDocumento';
import MenuModifica from './MenuModifica';
import CaricamentoMultiplo, { MAX_FILE, erroreCaricamento, lottoInCorso, useLotto } from './CaricamentoMultiplo';
import {
  CATEGORIE, caricaDocumento, chiestoQuando, consegna, conteggio, contiPersona, descriviVoce, giornoBreve, iconaVoce,
  leggiDocumento, prossimoSollecito, quando, raggruppaPerPersona, ricevutiPerPersona, salvaBlob, sceltePeriodo,
  scaricaZip, testoEntro, titoloPersona,
} from '../../utils/documenti';
import { adessoRoma } from '../../utils/percorso';

// Scheda cliente → Documenti (staff), nell'ordine chiesto dal committente:
//   1. Richieste aperte, per persona (cosa le è stato chiesto, da quanto, i solleciti)
//   2. Da verificare, con l'anteprima del documento accanto (mai confermare senza vedere: ConfermaDocumento)
//   3. Ricevuti, per persona e per categoria
//   4. Solo per noi
//   5. Invii (banca e notaio)
// Ogni riga di un documento ha gli stessi quattro comandi: Apri · Modifica · Scarica · Verifica/Rifiuta.
// I pezzi comuni al cliente arrivano già fatti: ConfermaDocumento (anteprima), MenuModifica (PUT /api/documenti/{id}),
// BottoniCarica (Carica e Fotografa, con il raddrizzamento della foto: prima lo staff non l'aveva).

const selectCls = 'w-full bg-card border border-gold/30 rounded-md px-3 py-2 text-sm text-white';
const erroreDi = (e, ripiego) => e?.response?.data?.detail && typeof e.response.data.detail === 'string' ? e.response.data.detail : ripiego;

const Pallino = ({ tipo }) => {
  const stili = { ok: 'border-green-500 text-green-400 bg-green-500/10', wait: 'border-amber-500 text-amber-400 bg-amber-500/10',
    sent: 'border-sky-400 text-sky-300 bg-sky-400/10', no: 'border-red-400 text-red-300 bg-red-400/10', vuoto: 'border-gold/20 text-white/40' };
  const Icona = tipo === 'ok' ? CheckCircle2 : tipo === 'sent' ? Send : tipo === 'no' ? X : tipo === 'wait' ? Clock : Plus;
  return <div className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center ${stili[tipo]}`}><Icona className="h-4 w-4" /></div>;
};

// Una riga: pallino, titolo, spiegazione, e sotto — larghi quanto la riga — i comandi (il pannello di "Modifica" ci
// sta dentro anche a 390 px, cosa che in una colonnina di bottoni non succedeva)
const Riga = ({ tipo, titolo, meta, extra, azioni, children, testid }) => (
  <div className="flex items-start gap-3 py-3 border-t border-gold/10 first:border-t-0 first:pt-0" data-testid={testid}>
    {tipo && <Pallino tipo={tipo} />}
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-white">{titolo}</div>
      {meta && <div className="text-xs text-muted-foreground mt-0.5">{meta}</div>}
      {extra}
      {azioni && <div className="flex flex-wrap items-center gap-1.5 mt-2">{azioni}</div>}
      {children}
    </div>
  </div>
);

const Gruppo = ({ nome }) => <p className="text-[11px] font-semibold text-white/40 uppercase tracking-wider mt-4 mb-1 first:mt-0">{nome}</p>;

// Il titoletto di una persona dentro una sezione: "Olena Prova · coniuge" con i suoi conteggi
const TestaPersona = ({ persona, conteggi }) => (
  <div className="flex flex-wrap items-baseline gap-x-2 mt-4 mb-1 first:mt-0" data-testid={`persona-${persona.id || 'senza'}`}>
    <p className="text-xs font-semibold text-gold">{titoloPersona(persona)}</p>
    <span className="text-[11px] text-muted-foreground">{contiPersona(conteggi || persona)}</span>
  </div>
);

const Chip = ({ attivo, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3 py-1 rounded-full border text-xs min-h-[32px] transition-colors ${attivo ? 'bg-gold text-black border-gold' : 'border-gold/30 text-muted-foreground hover:border-gold/60'}`}>
    {children}
  </button>
);

// ---------------------------------------------------------------- la riga di un documento (Apri · Modifica · Scarica · Verifica/Rifiuta)

export const nomeDelFile = (doc) => doc.nome_file || doc.nome_originale
  || `${(doc.tipo_nome || 'documento').replace(/\s+/g, '_')}${(doc.content_type || '').includes('pdf') ? '.pdf' : ''}`;

const RigaDocumento = ({
  doc, clienteId, tipi, persone, onFatto, tipoPallino = null, titolo = null, meta = null, extra = null,
  compatta = false, conVerifica = true, apertaDiBase = false, azioniExtra = null,
}) => {
  const [aperto, setAperto] = useState(apertaDiBase);
  const [rifiuto, setRifiuto] = useState(null);
  const [accessi, setAccessi] = useState([]);

  // chi l'ha aperto e scaricato: si legge quando si guarda il documento, non prima (una chiamata per riga sarebbe
  // una chiamata per ogni documento della pratica)
  useEffect(() => {
    if (!aperto) return;
    axios.get(`${API}/documenti/${doc.id}/accessi`).then((r) => setAccessi(r.data || [])).catch(() => setAccessi([]));
  }, [aperto, doc.id]);

  const chiama = async (metodo, url, corpo, ok) => {
    try { await axios[metodo](url, corpo); toast.success(ok); if (onFatto) await onFatto(); } catch (e) { toast.error(erroreDi(e, 'Non riuscito')); }
  };
  const scarica = async () => {
    try { salvaBlob(await leggiDocumento(doc, 'scarica'), nomeDelFile(doc)); } catch { toast.error('Non siamo riusciti a scaricarlo'); }
  };
  const daDecidere = conVerifica && doc.stato !== 'verificato' && doc.stato !== 'rifiutato';
  const nome = doc.tipo_nome || doc.nome_file || 'il documento';

  const verificaERifiuta = (dentroAnteprima) => (daDecidere ? (
    <>
      {doc.tipo ? (
        dentroAnteprima
          ? <button type="button" onClick={() => chiama('put', `${API}/documenti/${doc.id}/verifica`, {}, 'Verificato')}>Verifica</button>
          : <Button size="sm" className="bg-gold text-black hover:bg-gold/90" onClick={() => chiama('put', `${API}/documenti/${doc.id}/verifica`, {}, 'Verificato')}>Verifica</Button>
      ) : null}
      {dentroAnteprima
        ? <button type="button" onClick={() => setRifiuto('')}>Rifiuta</button>
        : <Button size="sm" variant="ghost" className="text-red-400" onClick={() => setRifiuto('')}>Rifiuta</Button>}
    </>
  ) : null);

  const formRifiuto = rifiuto !== null && (
    <div className="flex flex-wrap gap-2 mt-2">
      <Input autoFocus className="bg-card border-gold/30 h-9 text-sm flex-1 min-w-[180px]" value={rifiuto}
        onChange={(e) => setRifiuto(e.target.value)} aria-label={`Motivo del rifiuto di ${nome}`}
        placeholder="Motivo per il cliente (es. foto sfocata)" />
      <Button size="sm" className="bg-red-500/80 hover:bg-red-500" disabled={rifiuto.trim().length < 3}
        onClick={() => { setRifiuto(null); chiama('put', `${API}/documenti/${doc.id}/rifiuta`, { motivo: rifiuto.trim() }, 'Rifiutato: il cliente lo ricarica'); }}>Invia</Button>
      <Button size="sm" variant="ghost" onClick={() => setRifiuto(null)}>Annulla</Button>
    </div>
  );

  const azioni = (
    <>
      {!aperto && (
        <Button size="sm" variant="outline" className="border-gold/30" onClick={() => setAperto(true)}
          aria-label={`Apri ${nome}`}>Apri</Button>
      )}
      <MenuModifica documento={doc} clienteId={clienteId} comeStaff persone={persone} tipi={tipi} onFatto={onFatto} />
      <Button size="sm" variant="ghost" onClick={scarica} aria-label={`Scarica ${nome}`}>Scarica</Button>
      {!aperto && verificaERifiuta(false)}
      {azioniExtra}
    </>
  );

  const anteprima = aperto && (
    <div className="mt-2">
      <ConfermaDocumento documento={doc} clienteId={clienteId} compatto={false} onChiudi={() => setAperto(false)}
        azioni={(
          <>
            {verificaERifiuta(true)}
            <button type="button" onClick={scarica}>Scarica</button>
            {accessi.length > 0 && (
              <small className="block w-full text-[11px] text-muted-foreground">
                {`Aperture e download: ${accessi.slice(0, 6).map((a) => `${a.chi?.nome || a.chi?.tipo} ${quando(a.quando).toLowerCase()} (${a.azione})`).join(' · ')}`}
              </small>
            )}
          </>
        )} />
    </div>
  );

  if (compatta) {
    return (
      <div className="py-1.5" data-testid={`riga-doc-${doc.id}`}>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-white/80 mr-1">{titolo || doc.periodo_testo || quando(doc.data_upload) || nome}</span>
          {azioni}
        </div>
        {meta && <div className="text-[11px] text-muted-foreground mt-0.5">{meta}</div>}
        {extra}
        {formRifiuto}
        {anteprima}
      </div>
    );
  }
  return (
    <Riga testid={`riga-doc-${doc.id}`} tipo={tipoPallino} titolo={titolo || nome} meta={meta}
      extra={<>{extra}{formRifiuto}</>} azioni={azioni}>
      {anteprima}
    </Riga>
  );
};

// ---------------------------------------------------------------- chiedi documenti

const ChiediDocumenti = ({ clienteId, tipi, catalogo, persone, onFatto, onChiudi }) => {
  const perProfilo = useMemo(() => new Set(catalogo.per_profilo), [catalogo]);
  const [scelti, setScelti] = useState(new Set(catalogo.preselezionati));
  const [tutti, setTutti] = useState(false);
  const [nota, setNota] = useState('');
  const [entro, setEntro] = useState('');
  // A chi lo chiediamo: il titolare o un familiare con documenti propri (il server la intesta a lui e il documento
  // di un altro non chiude la sua richiesta). Di partenza il titolare; "" = a tutto il nucleo, come le richieste
  // di prima, che non dicevano di chi erano.
  const [persona, setPersona] = useState('titolare');
  const [attesa, setAttesa] = useState(false);
  const visibili = tipi.filter((t) => t.chi_carica === 'cliente' && t.visibile_cliente && (tutti || perProfilo.has(t.id) || scelti.has(t.id)));
  const cambia = (id) => { const s = new Set(scelti); if (s.has(id)) s.delete(id); else s.add(id); setScelti(s); };
  const invia = async () => {
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/clienti/${clienteId}/richieste`,
        { tipi: [...scelti], nota: nota.trim() || null, entro: entro || null, persona_id: persona });
      toast.success(r.data.voci?.length ? 'Richiesta inviata: email, notifica e campanella' : 'Ci sono già tutti: nessuna richiesta');
      onFatto();
      onChiudi();
    } catch (e) { toast.error(erroreDi(e, 'Richiesta non inviata')); } finally { setAttesa(false); }
  };
  return (
    <Card className="card-luxury" data-testid="chiedi-documenti">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-display text-gold flex items-center gap-2"><Plus className="h-4 w-4" />Chiedi documenti al cliente
          <span className="text-xs text-muted-foreground font-sans font-normal ml-2">spunta e invia: il cliente riceve email e notifica</span></CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {visibili.map((t) => <Chip key={t.id} attivo={scelti.has(t.id)} onClick={() => cambia(t.id)} testid={`chiedi-${t.id}`}>{t.nome}</Chip>)}
          {!tutti && <Chip onClick={() => setTutti(true)}>Altri…</Chip>}
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">A chi
            <select className="bg-card border border-gold/30 rounded-md px-2 py-1 text-sm text-white min-w-[180px]"
              value={persona} onChange={(e) => setPersona(e.target.value)} aria-label="A chi li chiediamo" data-testid="chiedi-a-chi">
              {(persone || []).map((p) => <option key={p.valore} value={p.valore}>{p.etichetta}</option>)}
              {(persone || []).length > 1 && <option value="">A tutto il nucleo</option>}
            </select>
          </label>
          <Input className="flex-1 min-w-[220px] bg-card border-gold/30" value={nota} onChange={(e) => setNota(e.target.value)}
            placeholder="Nota per il cliente (es. anche screenshot dall'app della banca)" />
          <label className="flex items-center gap-2 text-xs text-muted-foreground">Entro il
            <Input type="date" className="bg-card border-gold/30 w-40" value={entro} onChange={(e) => setEntro(e.target.value)} aria-label="Entro il (facoltativo)" />
          </label>
          <Button onClick={invia} disabled={attesa || !scelti.size} className="bg-gold text-black hover:bg-gold/90">Invia richiesta</Button>
          <Button variant="ghost" onClick={onChiudi}>Annulla</Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- carica (staff), con fotocamera e raddrizzamento

const CaricaStaff = ({ clienteId, tipi, persone, tipoIniziale, onFatto, onChiudi }) => {
  const [tipo, setTipo] = useState(tipoIniziale || '');
  const [persona, setPersona] = useState('titolare');
  const [periodo, setPeriodo] = useState('');
  const [attesa, setAttesa] = useState(false);
  const info = tipi.find((t) => t.id === tipo);
  const scelte = info?.periodico ? sceltePeriodo(info.periodico.regola, adessoRoma()) : [];
  const invia = async (files, opzioni) => {
    setAttesa(true);
    try {
      await caricaDocumento(clienteId, files, {
        tipo: tipo || undefined, personaId: persona, periodo: periodo ? JSON.parse(periodo) : undefined, comePdf: opzioni?.comePdf,
      });
      toast.success('Caricato');
      onFatto();
      onChiudi();
    } catch (e) { toast.error(erroreCaricamento(e, 'Caricamento non riuscito')); } finally { setAttesa(false); }
  };
  return (
    <Card className="card-luxury" data-testid="carica-staff">
      <CardHeader className="pb-2"><CardTitle className="text-base font-display text-gold flex items-center gap-2">Carica un documento</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <select className={selectCls} value={tipo} onChange={(e) => { setTipo(e.target.value); setPeriodo(''); }} aria-label="Tipo di documento">
            <option value="">Da classificare</option>
            {Object.keys(CATEGORIE).map((c) => (
              <optgroup key={c} label={CATEGORIE[c]}>
                {tipi.filter((t) => t.categoria === c).map((t) => <option key={t.id} value={t.id}>{t.nome}{t.visibile_cliente ? '' : ' (solo per noi)'}</option>)}
              </optgroup>
            ))}
          </select>
          <select className={selectCls} value={persona} onChange={(e) => setPersona(e.target.value)} aria-label="Di chi è">
            {persone.map((p) => <option key={p.valore} value={p.valore}>{p.etichetta}</option>)}
          </select>
          {scelte.length > 0 && (
            <select className={selectCls} value={periodo} onChange={(e) => setPeriodo(e.target.value)} aria-label="Periodo">
              <option value="">Periodo: lo legge dal documento</option>
              {scelte.map((s) => <option key={s.etichetta} value={JSON.stringify(s.periodo)}>{s.etichetta}</option>)}
            </select>
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <BottoniCarica onFile={invia} multiplo comeStaff attesa={attesa} etichette={{ file: 'Scegli i file', foto: 'Fotografa' }} />
          <Button variant="ghost" onClick={onChiudi}>Annulla</Button>
          <span className="text-xs text-muted-foreground">Più file diventano un PDF · massimo 10 MB · le foto si raddrizzano prima di partire</span>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- 1. richieste aperte, per persona

// Le voci che mancano raggruppate per persona del nucleo; in fondo quelle di una persona che non c'è (più)
export const richiestePerPersona = (persone, voci) => {
  const gruppi = [];
  const presi = new Set();
  (persone || []).forEach((p) => {
    const sue = (voci || []).filter((v) => (v.persona || 'titolare') === p.id);
    sue.forEach((v) => presi.add(v));
    if (sue.length) gruppi.push({ persona: p, voci: sue });
  });
  const altre = (voci || []).filter((v) => !presi.has(v));
  if (altre.length) gruppi.push({ persona: { id: null, nome: null, senza_persona: true }, voci: altre });
  return gruppi;
};

const RichiesteAperte = ({ dati, onFatto }) => {
  const persone = (dati.per_persona && dati.per_persona.length ? dati.per_persona : dati.persone) || [];
  const gruppi = richiestePerPersona(persone, dati.richiesti || []);
  const daFirmare = dati.da_firmare || [];
  const richiestaDi = (id) => (dati.richieste || []).find((r) => r.id === id);
  const sollecita = async (id) => {
    try { await axios.post(`${API}/richieste/${id}/sollecita`); toast.success('Promemoria inviato'); await onFatto(); } catch (e) { toast.error(erroreDi(e, 'Non riuscito')); }
  };
  const fermaSolleciti = async (id, fermati) => {
    try { await axios.put(`${API}/richieste/${id}`, { solleciti_fermati: fermati }); await onFatto(); } catch (e) { toast.error(erroreDi(e, 'Non riuscito')); }
  };
  return (
    <Card className="card-luxury" data-testid="richieste-aperte">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-display text-gold flex items-center gap-2"><Users className="h-4 w-4" />Richieste aperte, per persona</CardTitle>
      </CardHeader>
      <CardContent>
        {!gruppi.length && !daFirmare.length && <p className="text-sm text-muted-foreground">Nessuna richiesta aperta: non aspettiamo niente dal cliente.</p>}
        {gruppi.map(({ persona, voci }) => {
          const aperte = new Set(voci.map((v) => v.richiesta_id).filter(Boolean));
          return (
            <div key={persona.id || 'senza-persona'}>
              <TestaPersona persona={persona}
                conteggi={{ ricevuti: persona.ricevuti, mancano: persona.mancano ?? voci.length, richieste_aperte: persona.richieste_aperte ?? aperte.size }} />
              {voci.map((v) => {
                const r = richiestaDi(v.richiesta_id);
                const fermati = r?.solleciti?.fermati;
                return (
                  <Riga key={`${v.tipo}-${v.persona || 'titolare'}`} tipo={v.rifiuto || v.scaduto ? 'no' : 'vuoto'} titolo={v.nome}
                    meta={<>{v.dettaglio && `${v.dettaglio} · `}
                      {/* rifiutato o scaduto senza una richiesta aperta: resta da caricare (prima spariva) */}
                      {v.da_rifare ? 'da ricaricare' : v.di_base ? 'dal profilo (mai chiesto)' : chiestoQuando(v.data_richiesta)}
                      {/* di chi è la richiesta: la si intesta a una persona dal pannello «Chiedi documenti», e solo
                          il suo documento la chiude; senza persona vale per tutto il nucleo, come quelle di prima */}
                      {r?.persona && ` · a ${r.persona}`}
                      {v.scaduto && <span className="text-red-300"> · {v.scaduto.testo}</span>}
                      {v.entro && ` · ${testoEntro(v.entro)?.testo || ''}`}
                      {r && !fermati && r.prossimo_sollecito && ` · ${prossimoSollecito(r.prossimo_sollecito)}`}
                      {r && ` · solleciti ${r.solleciti?.inviati || 0}/3`}{fermati && ' · solleciti fermati'}
                      {v.nota && ` · nota: ${v.nota}`}
                      {v.rifiuto?.motivo && <span className="text-red-300"> · rifiutato: {v.rifiuto.motivo}</span>}</>}
                    azioni={r ? (
                      <>
                        <Button size="sm" variant="outline" className="border-gold/30" onClick={() => sollecita(r.id)}>Sollecita ora</Button>
                        <Button size="sm" variant="ghost" onClick={() => fermaSolleciti(r.id, !fermati)}>{fermati ? 'Riprendi solleciti' : 'Ferma solleciti'}</Button>
                      </>
                    ) : null} />
                );
              })}
            </div>
          );
        })}
        {daFirmare.length > 0 && <Gruppo nome="Da firmare" />}
        {daFirmare.map((f) => <Riga key={f.id} tipo="wait" titolo={f.nome} meta={`mandato ${quando(f.data).toLowerCase()}`} />)}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- 2. da verificare (con l'anteprima accanto)

const DaVerificare = ({ dati, clienteId, tipi, onFatto }) => (
  <Card className="card-luxury" data-testid="da-verificare">
    <CardHeader className="pb-2">
      <CardTitle className="text-base font-display text-gold flex items-center gap-2"><Clock className="h-4 w-4" />Da verificare
        <span className="text-xs text-muted-foreground font-sans font-normal ml-2">si guarda il documento, poi si conferma</span></CardTitle>
    </CardHeader>
    <CardContent>
      {!dati.da_verificare.length && <p className="text-sm text-muted-foreground">Niente da verificare: siamo in pari.</p>}
      {dati.da_verificare.map((d, i) => {
        const cl = d.classificazione || {};
        return (
          <RigaDocumento key={d.id} doc={d} clienteId={clienteId} tipi={tipi} persone={dati.persone} onFatto={onFatto}
            tipoPallino="wait" apertaDiBase={i === 0}
            titolo={<>{d.caricato_da?.tipo === 'cliente' && <Badge className="bg-gold/20 text-gold border-gold/30 text-[10px] mr-2">nuovo</Badge>}
              {d.tipo_nome}{d.persona && d.persona !== 'titolare' ? ` · ${d.persona}` : ''}</>}
            meta={<>
              caricato {d.caricato_da?.tipo === 'cliente' ? 'dal cliente' : `da ${d.caricato_da?.nome || 'staff'}`} {quando(d.data_upload).toLowerCase()}
              {d.periodo_testo && ` · ${d.periodo_testo}`}{d.scadenza_testo && ` · ${d.scadenza_testo}`}
              {d.in_analisi && ' · lo sto leggendo…'}
            </>}
            extra={<>
              {cl.avviso && <p className="text-xs text-amber-400 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{cl.avviso.charAt(0).toUpperCase() + cl.avviso.slice(1)}</p>}
              {!d.tipo && <p className="text-xs text-amber-400 mt-1">Non sappiamo che documento è: ditelo con «Modifica», poi si può verificare.</p>}
            </>} />
        );
      })}
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------- 3. ricevuti, per persona e per categoria

const Versioni = ({ doc }) => {
  const [lista, setLista] = useState(null);
  if (!doc.versioni) return null;
  if (!lista) {
    return (
      <button type="button" className="text-[11px] text-gold underline"
        onClick={async () => setLista((await axios.get(`${API}/documenti/${doc.id}/versioni`)).data)}>versioni ({doc.versioni + 1})</button>
    );
  }
  return (
    <div className="mt-1 space-y-0.5">
      {lista.map((v) => (
        <p key={v.id} className="text-[11px] text-muted-foreground">
          {v.attuale ? 'attuale' : 'precedente'} · {quando(v.data_upload)}{v.caricato_da?.nome ? ` · ${v.caricato_da.nome}` : ''}
        </p>
      ))}
    </div>
  );
};

const VoceRicevuta = ({ voce, clienteId, tipi, persone, onFatto, conCategoria }) => {
  const docs = voce.documenti || [];
  const consegnato = consegna(voce);
  const scadenza = docs.find((d) => d.scadenza_testo);
  return (
    <div className="flex items-start gap-3 py-3 border-t border-gold/10 first:border-t-0 first:pt-0" data-testid={`voce-${voce.tipo || 'da-classificare'}-${docs[0]?.id || ''}`}>
      <Pallino tipo={iconaVoce(voce)} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white">
          {voce.nome}{!conCategoria && voce.persona && voce.persona !== 'titolare' ? ` · ${voce.persona}` : ''}
          {conCategoria && voce.categoria_nome && <span className="text-[11px] font-normal text-white/40 ml-2">{voce.categoria_nome}</span>}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {descriviVoce(voce)}{consegnato && ` · ${consegnato}`}
          {scadenza && <span className={scadenza.scadenza_stato === 'scaduto' ? 'text-red-400' : scadenza.scadenza_stato === 'presto' ? 'text-amber-400' : ''}> · {scadenza.scadenza_testo}</span>}
          {voce.periodico && (voce.periodico.aggiornata
            ? <span className="text-green-400"> · copertura aggiornata a oggi ✓</span>
            : voce.periodico.manca && <span className="text-amber-400"> · manca: {voce.periodico.manca}</span>)}
        </div>
        <div className="mt-1">
          {docs.map((d) => (
            <RigaDocumento key={d.id} compatta doc={d} clienteId={clienteId} tipi={tipi} persone={persone} onFatto={onFatto}
              titolo={d.periodo_testo || quando(d.data_upload) || d.tipo_nome}
              extra={<Versioni doc={d} />} />
          ))}
        </div>
      </div>
    </div>
  );
};

const Ricevuti = ({ dati, clienteId, tipi, onFatto, nomeCliente }) => {
  // per persona quando il nucleo è più di uno (o c'è un documento di cui non si sa di chi è), se no per categoria
  const perPersona = raggruppaPerPersona(dati.per_persona);
  const gruppi = perPersona ? ricevutiPerPersona(dati.ricevuti, dati.per_persona) : [];
  const comuni = { clienteId, tipi, persone: dati.persone, onFatto };
  return (
    <Card className="card-luxury" data-testid="ricevuti-staff">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base font-display text-gold flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Ricevuti</CardTitle>
        <button type="button" className="text-xs text-gold" onClick={() => scaricaZip(`${API}/clienti/${clienteId}/documenti/zip`, `documenti-${nomeCliente}.zip`)}>
          <Download className="h-3 w-3 inline mr-1" />Scarica zip
        </button>
      </CardHeader>
      <CardContent>
        {!dati.ricevuti.length && <p className="text-sm text-muted-foreground">Ancora nessun documento.</p>}
        {perPersona
          ? gruppi.map((g) => (
            <div key={g.persona.id || 'senza-persona'}>
              <TestaPersona persona={g.persona} />
              {!g.voci.length && <p className="text-xs text-muted-foreground py-2">Ancora niente di suo.</p>}
              {g.voci.map((v) => <VoceRicevuta key={`${v.tipo}-${v.documenti[0]?.id}`} voce={v} conCategoria {...comuni} />)}
            </div>
          ))
          : dati.ricevuti.map((g) => (
            <div key={g.categoria}>
              <Gruppo nome={g.nome} />
              {g.voci.map((v) => <VoceRicevuta key={`${v.tipo}-${v.persona}-${v.documenti[0]?.id}`} voce={v} {...comuni} />)}
            </div>
          ))}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- 4. solo per noi

const SoloPerNoi = ({ dati, clienteId, tipi, onFatto, note, setNote, salvaNote }) => {
  // i tipi solo-staff sono tanti (perizia, APE, CDU, proposta, delibera, atto…): la sezione parla di quello che
  // c'è, e quelli da caricare si aprono quando servono
  const [mostraMancanti, setMostraMancanti] = useState(false);
  const quantiMancano = (dati.solo_staff || []).reduce((n, g) => n + g.mancanti.length, 0);
  const carica = async (tipoId, files, opzioni) => {
    try {
      await caricaDocumento(clienteId, files, { tipo: tipoId, personaId: 'titolare', comePdf: opzioni?.comePdf });
      toast.success('Caricato');
      await onFatto();
    } catch (e) { toast.error(erroreCaricamento(e, 'Caricamento non riuscito')); }
  };
  return (
    <Card className="card-luxury border-sky-400/40" data-testid="solo-per-noi">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base font-display text-sky-300 flex items-center gap-2"><Lock className="h-4 w-4" />Solo per noi</CardTitle>
        <span className="text-xs text-sky-300 font-bold">il cliente non li vede</span>
      </CardHeader>
      <CardContent>
        {dati.solo_staff.map((g) => (g.documenti.length || mostraMancanti) && (
          <div key={g.categoria}>
            <Gruppo nome={g.nome} />
            {g.documenti.map((d) => (
              <RigaDocumento key={d.id} doc={d} clienteId={clienteId} tipi={tipi} persone={dati.persone} onFatto={onFatto}
                tipoPallino={d.stato === 'verificato' ? 'ok' : 'wait'} titolo={d.tipo_nome}
                meta={`${d.caricato_da?.nome ? `da ${d.caricato_da.nome} · ` : ''}${giornoBreve(d.data_upload)}${d.scadenza_testo ? ` · ${d.scadenza_testo}` : ''}`} />
            ))}
            {mostraMancanti && g.mancanti.map((t) => (
              <Riga key={t.id} tipo="vuoto" titolo={t.nome} meta="da caricare"
                azioni={<BottoniCarica comeStaff multiplo etichette={{ file: 'Carica', foto: 'Fotografa' }}
                  onFile={(files, opzioni) => carica(t.id, files, opzioni)} />} />
            ))}
          </div>
        ))}
        {quantiMancano > 0 && (
          <button type="button" className="text-xs text-sky-300 underline mt-3" onClick={() => setMostraMancanti((x) => !x)}
            aria-expanded={mostraMancanti}>
            {mostraMancanti ? 'Nascondi quelli da caricare' : `Da caricare: ${quantiMancano === 1 ? 'un altro' : `altri ${quantiMancano}`} (perizia, APE, delibera…)`}
          </button>
        )}
        <Gruppo nome="Note interne" />
        <Textarea className="bg-card border-gold/20 text-sm" rows={3} value={note} onChange={(e) => setNote(e.target.value)} onBlur={salvaNote}
          aria-label="Note interne"
          placeholder="Es. contratto a tempo indeterminato da 3 anni; la banca vuole gli estratti aggiornati prima della delibera" />
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- 5. invii (banca e notaio)

const InviaDocumenti = ({ clienteId, nomeCliente, documenti, invii, onFatto }) => {
  const [destinatari, setDestinatari] = useState([]);
  const [dest, setDest] = useState('');
  const [nuovo, setNuovo] = useState({ nome: '', ente: '', email: '', telefono: '', tipo: 'banca' });
  const [scelti, setScelti] = useState(new Set());
  const [giorni, setGiorni] = useState(7);
  const [messaggio, setMessaggio] = useState('');
  const [attesa, setAttesa] = useState(false);
  useEffect(() => { axios.get(`${API}/destinatari`).then((r) => setDestinatari(r.data)).catch(() => {}); }, []);
  // Spuntati quelli mai mandati a quel destinatario
  useEffect(() => {
    const giaMandati = new Set(invii.filter((i) => i.destinatario_id === dest).flatMap((i) => i.documenti || []));
    setScelti(new Set(documenti.filter((d) => !giaMandati.has(d.id)).map((d) => d.id)));
    const d = destinatari.find((x) => x.id === dest);
    setMessaggio(d ? `Gentile ${d.nome || d.ente}, ecco i documenti per la pratica di ${nomeCliente}.` : '');
  }, [dest, invii, documenti, destinatari, nomeCliente]);
  const cambia = (id) => { const s = new Set(scelti); if (s.has(id)) s.delete(id); else s.add(id); setScelti(s); };
  const invia = async () => {
    setAttesa(true);
    try {
      const corpo = { documenti: [...scelti], giorni, messaggio };
      if (dest === 'nuovo') corpo.destinatario = nuovo; else corpo.destinatario_id = dest;
      await axios.post(`${API}/clienti/${clienteId}/invii`, corpo);
      toast.success('Inviato: il destinatario riceve un link protetto');
      setDest('');
      onFatto();
    } catch (e) { toast.error(erroreDi(e, 'Invio non riuscito')); } finally { setAttesa(false); }
  };
  return (
    <div className="space-y-3" id="invia-documenti" data-testid="invia-documenti">
      <div>
        <label className="text-xs text-muted-foreground" htmlFor="dest">Destinatario</label>
        <select id="dest" className={selectCls} value={dest} onChange={(e) => setDest(e.target.value)}>
          <option value="">Scegliete…</option>
          {destinatari.map((d) => <option key={d.id} value={d.id}>{[d.ente, d.nome, d.email].filter(Boolean).join(' · ')}</option>)}
          <option value="nuovo">Nuovo destinatario…</option>
        </select>
      </div>
      {dest === 'nuovo' && (
        <div className="grid grid-cols-2 gap-2">
          <Input className="bg-card border-gold/30" placeholder="Ente (banca, notaio…)" value={nuovo.ente} onChange={(e) => setNuovo({ ...nuovo, ente: e.target.value })} />
          <Input className="bg-card border-gold/30" placeholder="Nome e cognome" value={nuovo.nome} onChange={(e) => setNuovo({ ...nuovo, nome: e.target.value })} />
          <Input className="bg-card border-gold/30" placeholder="Email" type="email" value={nuovo.email} onChange={(e) => setNuovo({ ...nuovo, email: e.target.value })} />
          <Input className="bg-card border-gold/30" placeholder="Telefono" value={nuovo.telefono} onChange={(e) => setNuovo({ ...nuovo, telefono: e.target.value })} />
          <select className={selectCls} value={nuovo.tipo} onChange={(e) => setNuovo({ ...nuovo, tipo: e.target.value })} aria-label="Tipo di destinatario">
            <option value="banca">Banca</option><option value="notaio">Notaio</option><option value="altro">Altro</option>
          </select>
        </div>
      )}
      {dest && (
        <>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Documenti</p>
            <div className="flex flex-wrap gap-2">
              {documenti.map((d) => <Chip key={d.id} attivo={scelti.has(d.id)} onClick={() => cambia(d.id)}>{d.tipo_nome}{d.periodo_testo ? ` · ${d.periodo_testo}` : ''}</Chip>)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select className={selectCls} value={giorni} onChange={(e) => setGiorni(Number(e.target.value))} aria-label="Il link vale">
              <option value={7}>Il link vale 7 giorni</option><option value={3}>3 giorni</option><option value={30}>30 giorni</option>
            </select>
          </div>
          <Input className="bg-card border-gold/30" value={messaggio} onChange={(e) => setMessaggio(e.target.value)} placeholder="Messaggio" />
          <Button onClick={invia} disabled={attesa || !scelti.size} className="w-full bg-gold text-black hover:bg-gold/90">Invia il fascicolo</Button>
        </>
      )}
      <p className="text-xs text-muted-foreground">Il destinatario riceve un link protetto, non gli allegati. Vedete quando apre e cosa scarica; il link scade da solo.</p>
    </div>
  );
};

const STATI_INVIO = { inviato: 'bg-gold/20 text-gold', aperto: 'bg-sky-500/20 text-sky-300', scaricato: 'bg-green-500/20 text-green-400', scaduto: 'bg-white/10 text-white/50', revocato: 'bg-white/10 text-white/50' };

const Invii = ({ dati, clienteId, nomeCliente, documenti, onFatto }) => {
  const invii = dati.invii || [];
  const rinvia = async (id) => {
    try { await axios.post(`${API}/invii/${id}/rinvia`); toast.success('Link nuovo inviato'); await onFatto(); } catch (e) { toast.error(erroreDi(e, 'Non riuscito')); }
  };
  // Revoca: chiude il link subito, anche per chi l'ha già aperto. Non manda niente al destinatario e non si
  // torna indietro, quindi si chiede conferma. Chi può farlo lo dice il server (`puoi_revocare`): il permesso
  // sta in un posto solo, altrimenti il bottone e la rotta un giorno diranno cose diverse.
  const revoca = async (id, ente) => {
    if (!window.confirm(`Chiudere subito il link mandato a ${ente || 'questo destinatario'}?\n\n`
      + 'Da quel momento non apre più niente, nemmeno per chi lo ha già aperto. '
      + 'Il destinatario non riceve nessun avviso. Per riaprire servirà un invio nuovo.')) return;
    try { await axios.post(`${API}/invii/${id}/revoca`); toast.success('Link revocato'); await onFatto(); } catch (e) { toast.error(erroreDi(e, 'Non riuscito')); }
  };
  return (
    <Card className="card-luxury border-gold/50" data-testid="invii">
      <CardHeader className="pb-2"><CardTitle className="text-base font-display text-gold flex items-center gap-2"><Send className="h-4 w-4" />Invii a banca e notaio</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <InviaDocumenti clienteId={clienteId} nomeCliente={nomeCliente} documenti={documenti} invii={invii} onFatto={onFatto} />
        {invii.length > 0 && <Gruppo nome="Già inviati" />}
        {invii.map((i) => (
          <div key={i.id} className="text-sm border-l-2 border-gold/40 pl-3" data-testid={`invio-${i.id}`}>
            {/* un <div>: la pastiglia dello stato è a sua volta un <div> e dentro un <p> il browser chiude il paragrafo */}
            <div className="font-semibold text-white">{i.destinatario?.ente} · {conteggio(i.documenti.length, 'documento', 'documenti')} <Badge className={`${STATI_INVIO[i.stato]} border-0 text-[10px] ml-1`}>{i.stato}</Badge></div>
            <p className="text-xs text-muted-foreground">
              {giornoBreve(i.data)}{i.aperture?.length ? ` · aperto il ${giornoBreve(i.aperture[0].data)}` : ''}
              {i.download?.length ? ` · ${i.download.length} download` : ''} · {i.stato === 'scaduto' ? 'scaduto' : `scade il ${giornoBreve(i.scade)}`}
            </p>
            <div className="flex gap-3 mt-1">
              {(i.stato === 'scaduto' || i.stato === 'aperto' || i.stato === 'scaricato') && (
                <button type="button" className="text-xs text-gold underline" onClick={() => rinvia(i.id)}>Rinvia (link nuovo)</button>
              )}
              {i.puoi_revocare && (
                <button type="button" className="text-xs text-white/50 underline" data-testid={`revoca-${i.id}`}
                        onClick={() => revoca(i.id, i.destinatario?.ente)}>Revoca</button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- fascicolo per la banca

const FascicoloBanca = ({ clienteId, nomeCliente, documenti, onChiudi }) => {
  const [destinatari, setDestinatari] = useState([]);
  const [dest, setDest] = useState('');
  const [scelti, setScelti] = useState(() => new Set(documenti.filter((d) => d.tipo).map((d) => d.id)));
  const [attesa, setAttesa] = useState(false);
  useEffect(() => { axios.get(`${API}/destinatari`).then((r) => { setDestinatari(r.data); const b = r.data.find((d) => d.tipo === 'banca'); if (b) setDest(b.id); }).catch(() => {}); }, []);
  const d = destinatari.find((x) => x.id === dest);
  const prepara = async () => {
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/clienti/${clienteId}/fascicolo`, { destinatario_id: dest, documenti: [...scelti] }, { responseType: 'blob' });
      salvaBlob(r.data, `${nomeCliente.replace(/\s+/g, '_')}_${(d?.ente || 'banca').replace(/\s+/g, '_')}.zip`);
      toast.success('Fascicolo pronto: zip con i nomi della banca e l\'indice');
    } catch { toast.error('Fascicolo non preparato'); } finally { setAttesa(false); }
  };
  const cambia = (id) => { const s = new Set(scelti); if (s.has(id)) s.delete(id); else s.add(id); setScelti(s); };
  return (
    <Card className="card-luxury" data-testid="fascicolo-banca">
      <CardHeader className="pb-2"><CardTitle className="text-base font-display text-gold flex items-center gap-2"><Download className="h-4 w-4" />Prepara fascicolo{d ? ` per ${d.ente}` : ''}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <select className={selectCls} value={dest} onChange={(e) => setDest(e.target.value)} aria-label="Banca o notaio">
          <option value="">Per chi?</option>
          {destinatari.map((x) => <option key={x.id} value={x.id}>{[x.ente, x.nome].filter(Boolean).join(' · ')}</option>)}
        </select>
        <div className="flex flex-wrap gap-2">
          {documenti.filter((x) => x.tipo).map((x) => <Chip key={x.id} attivo={scelti.has(x.id)} onClick={() => cambia(x.id)}>{x.tipo_nome}{x.persona && x.persona !== 'titolare' ? ` · ${x.persona}` : ''}{x.periodo_testo ? ` · ${x.periodo_testo}` : ''}</Chip>)}
        </div>
        <p className="text-xs text-muted-foreground">I nomi dei file seguono il modello della banca (Impostazioni → Rubrica banche e notai); senza modello: Cognome_Nome_CI.pdf. Nello zip c'è anche l'indice in PDF.</p>
        <div className="flex gap-2">
          <Button className="bg-gold text-black hover:bg-gold/90" disabled={!dest || !scelti.size || attesa} onClick={prepara}>{attesa ? 'Preparo…' : `Scarica lo zip (${scelti.size})`}</Button>
          <Button variant="ghost" onClick={onChiudi}>Chiudi</Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- famiglia: persone con i loro documenti

const RELAZIONI = [['coniuge', 'Coniuge'], ['convivente', 'Convivente'], ['figlio', 'Figlio'], ['figlia', 'Figlia'], ['genitore', 'Genitore'], ['altro', 'Altro']];
const Famiglia = ({ clienteId, persone, onFatto }) => {
  const [nuovo, setNuovo] = useState(null);
  const familiari = persone.filter((p) => p.id !== 'titolare');
  const salva = async () => {
    try { await axios.post(`${API}/clienti/${clienteId}/familiari`, nuovo); setNuovo(null); onFatto(); toast.success('Familiare aggiunto'); } catch (e) { toast.error(erroreDi(e, 'Non aggiunto')); }
  };
  const documenti = async (p, v) => { try { await axios.put(`${API}/clienti/${clienteId}/familiari/${p.id}`, { documenti: v }); onFatto(); } catch (e) { toast.error(erroreDi(e, 'Non salvato')); } };
  return (
    <Card className="card-luxury" data-testid="famiglia">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base font-display text-gold">Famiglia</CardTitle>
        {!nuovo && <button type="button" className="text-xs text-gold" onClick={() => setNuovo({ nome: '', cognome: '', relazione: 'coniuge', data_di_nascita: '' })}>+ Familiare</button>}
      </CardHeader>
      <CardContent className="space-y-2">
        {!familiari.length && !nuovo && <p className="text-sm text-muted-foreground">Nessun familiare. Chi ha "documenti propri" ha una riga per carta d'identità, codice fiscale e gli altri documenti per persona.</p>}
        {familiari.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-white font-semibold">{p.nome}</span>
            <span className="text-xs text-muted-foreground">{p.etichetta || p.relazione}{p.data_di_nascita ? ` · nato il ${p.data_di_nascita.split('-').reverse().join('/')}` : ''}</span>
            <label className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
              <input type="checkbox" checked={p.documenti} onChange={(e) => documenti(p, e.target.checked)} />documenti propri
            </label>
          </div>
        ))}
        {nuovo && (
          <div className="grid grid-cols-2 gap-2">
            <Input className="bg-card border-gold/30" placeholder="Nome" value={nuovo.nome} onChange={(e) => setNuovo({ ...nuovo, nome: e.target.value })} />
            <Input className="bg-card border-gold/30" placeholder="Cognome" value={nuovo.cognome} onChange={(e) => setNuovo({ ...nuovo, cognome: e.target.value })} />
            <select className={selectCls} value={nuovo.relazione} onChange={(e) => setNuovo({ ...nuovo, relazione: e.target.value })} aria-label="Relazione">
              {RELAZIONI.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
            </select>
            <Input type="date" className="bg-card border-gold/30" value={nuovo.data_di_nascita} onChange={(e) => setNuovo({ ...nuovo, data_di_nascita: e.target.value })} aria-label="Data di nascita" />
            <div className="col-span-2 flex gap-2">
              <Button size="sm" className="bg-gold text-black hover:bg-gold/90" disabled={!nuovo.nome.trim()} onClick={salva}>Aggiungi</Button>
              <Button size="sm" variant="ghost" onClick={() => setNuovo(null)}>Annulla</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- il componente

const NOMI_CAMPI = { data_di_nascita: 'Data di nascita', numero_documento: 'Numero del documento', nazionalita: 'Nazionalità' };

const DocumentiPratica = ({ clienteId }) => {
  const [dati, setDati] = useState(null);
  const [tipi, setTipi] = useState([]);
  const [pannello, setPannello] = useState(null); // "chiedi" | "fascicolo" | {carica: tipo}
  const [note, setNote] = useState('');
  const [sopra, setSopra] = useState(false);

  const carica = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([axios.get(`${API}/clienti/${clienteId}/documenti/pratica`), axios.get(`${API}/documenti/catalogo`)]);
      setDati(p.data);
      setTipi(c.data.tipi);
      setNote(p.data.note_documenti || '');
    } catch (e) { toast.error(erroreDi(e, 'Documenti non caricati')); }
  }, [clienteId]);
  useEffect(() => { carica(); }, [carica]);
  const { lotto, avvia, leggi, chiudi } = useLotto(clienteId, carica);

  const tuttiIDocumenti = useMemo(() => {
    if (!dati) return [];
    return [...dati.ricevuti.flatMap((g) => g.voci.flatMap((v) => v.documenti)), ...dati.solo_staff.flatMap((g) => g.documenti)]
      .filter((d) => d.stato !== 'rifiutato');
  }, [dati]);

  if (!dati) return <p className="text-sm text-muted-foreground">Carico i documenti…</p>;
  const nomeCliente = `${dati.cliente.nome || ''} ${dati.cliente.cognome || ''}`.trim();
  const nucleo = dati.persone || [{ id: 'titolare', nome: nomeCliente, relazione: 'titolare' }];
  const voceMenu = (p) => ({ valore: p.id, etichetta: p.id === 'titolare' ? `${p.nome} (titolare)` : `${p.nome} (${p.etichetta || p.relazione})` });
  const persone = nucleo.map(voceMenu);
  // A chi si chiedono i documenti: il titolare e chi ha documenti propri (le stesse persone del server,
  // familiari.persone_con_documenti). Caricare, invece, si può per chiunque: la carta d'identità del figlio arriva
  // lo stesso.
  const personeRichiesta = nucleo.filter((p) => p.id === 'titolare' || p.documenti !== false).map(voceMenu);
  const salvaNote = async () => { try { await axios.put(`${API}/clienti/${clienteId}/note-documenti`, { testo: note }); toast.success('Note salvate'); } catch { toast.error('Note non salvate'); } };
  const decidi = async (p, accetta) => { await axios.put(`${API}/clienti/${clienteId}/proposte-dati/${p.id}`, { accetta }); carica(); };

  return (
    <div className={`space-y-4 rounded-xl ${sopra ? 'ring-2 ring-gold/60' : ''}`} data-testid="documenti-pratica"
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setSopra(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setSopra(false); }}
      onDrop={(e) => { const l = Array.from(e.dataTransfer?.files || []); if (!l.length) return; e.preventDefault(); setSopra(false); if (!lottoInCorso(lotto)) avvia(l); }}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[.18em] text-gold font-bold">Documenti · {nomeCliente}</p>
          <h2 className="text-2xl font-display text-white mt-1">
            {dati.mancano} richiest{dati.mancano === 1 ? 'o' : 'i'}, <span className="text-gold italic">{dati.da_verificare.length} da verificare</span>
          </h2>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" className="border-gold/30" onClick={() => setPannello(pannello === 'chiedi' ? null : 'chiedi')}>Chiedi documenti</Button>
          {/* Carica e Fotografa: gli stessi due bottoni del cliente, con il raddrizzamento della foto (prima lo staff
              poteva solo scegliere un file) */}
          <BottoniCarica comeStaff multiplo attesa={lottoInCorso(lotto)} etichette={{ file: `Carica (fino a ${MAX_FILE})`, foto: 'Fotografa' }}
            onFile={(files) => avvia(files)} />
          <Button variant="ghost" onClick={() => setPannello({ carica: '' })}>Carica con tipo</Button>
          <Button variant="outline" className="border-gold/30" onClick={() => setPannello(pannello === 'fascicolo' ? null : 'fascicolo')}>
            <Download className="h-4 w-4 mr-1" />Prepara fascicolo
          </Button>
          <Button className="bg-gold text-black hover:bg-gold/90" onClick={() => document.getElementById('invia-documenti')?.scrollIntoView({ behavior: 'smooth' })}>
            <Send className="h-4 w-4 mr-1" />Invia a banca / notaio
          </Button>
        </div>
      </div>

      {lotto && (
        <CaricamentoMultiplo lotto={lotto} variante="staff" tipi={tipi} persone={dati.persone} onLeggi={leggi} onChiudi={chiudi}
          onConfermato={() => { carica(); toast.success('Confermati e verificati'); }} />
      )}
      {pannello === 'fascicolo' && <FascicoloBanca clienteId={clienteId} nomeCliente={nomeCliente} documenti={tuttiIDocumenti} onChiudi={() => setPannello(null)} />}
      {pannello === 'chiedi' && (
        <ChiediDocumenti clienteId={clienteId} tipi={tipi} catalogo={dati.catalogo} persone={personeRichiesta}
          onFatto={carica} onChiudi={() => setPannello(null)} />
      )}
      {pannello?.carica !== undefined && (
        <CaricaStaff key={pannello.carica} clienteId={clienteId} tipi={tipi} persone={persone} tipoIniziale={pannello.carica} onFatto={carica} onChiudi={() => setPannello(null)} />
      )}

      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr] items-start">
        <div className="space-y-4 min-w-0">
          <RichiesteAperte dati={dati} onFatto={carica} />
          <DaVerificare dati={dati} clienteId={clienteId} tipi={tipi} onFatto={carica} />
          <Ricevuti dati={dati} clienteId={clienteId} tipi={tipi} onFatto={carica} nomeCliente={nomeCliente} />
          <SoloPerNoi dati={dati} clienteId={clienteId} tipi={tipi} onFatto={carica} note={note} setNote={setNote} salvaNote={salvaNote} />
          <Invii dati={dati} clienteId={clienteId} nomeCliente={nomeCliente} documenti={tuttiIDocumenti} onFatto={carica} />
        </div>

        <div className="space-y-4 min-w-0">
          <Famiglia clienteId={clienteId} persone={dati.persone || []} onFatto={carica} />
          {dati.proposte_dati.length > 0 && (
            <Card className="card-luxury border-amber-500/40" data-testid="proposte-dati">
              <CardHeader className="pb-2"><CardTitle className="text-base font-display text-amber-300 flex items-center gap-2"><FileText className="h-4 w-4" />Dati letti dai documenti, da confermare</CardTitle></CardHeader>
              <CardContent>
                {dati.proposte_dati.map((p) => (
                  <Riga key={p.id} tipo="wait" titolo={`${NOMI_CAMPI[p.campo] || p.campo}${p.persona !== 'titolare' ? ` · ${p.persona}` : ''}: ${p.valore}`}
                    meta={`scritto a mano: ${p.attuale}`}
                    azioni={<>
                      <Button size="sm" className="bg-gold text-black hover:bg-gold/90" onClick={() => decidi(p, true)}>Usa questo</Button>
                      <Button size="sm" variant="ghost" onClick={() => decidi(p, false)}>Tieni quello scritto</Button>
                    </>} />
                ))}
              </CardContent>
            </Card>
          )}
          {dati.scadenze.length > 0 && (
            <Card className="card-luxury" data-testid="scadenze">
              <CardHeader className="pb-2"><CardTitle className="text-base font-display text-gold flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Scadenze</CardTitle></CardHeader>
              <CardContent>
                {dati.scadenze.map((s) => (
                  <Riga key={s.documento_id} tipo={s.stato === 'scaduto' ? 'no' : 'wait'} titolo={`${s.tipo_nome}${s.persona !== 'titolare' ? ` · ${s.persona}` : ''}`} meta={s.testo} />
                ))}
              </CardContent>
            </Card>
          )}
          {dati.cronologia.length > 0 && (
            <Card className="card-luxury" data-testid="cronologia">
              <CardHeader className="pb-2"><CardTitle className="text-base font-display text-gold flex items-center gap-2"><History className="h-4 w-4" />Cronologia</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {dati.cronologia.slice(0, 10).map((e, i) => <div key={`${e.data}-${i}`} className="text-sm"><p className="text-white">{e.testo}</p><p className="text-xs text-muted-foreground">{quando(e.data)}</p></div>)}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentiPratica;
